import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { showToast } from '@restworld/ui-ds-base';
import { JobApplicationReport } from '@restworld/utility-types';
import { QUERY_KEYS, useAnalytics } from '@restworld/utils-common';

import {
  candidateModalVariantAtom,
  CandidateModalVariantType,
  isCandidateModalSuccessScreenAtom,
  reportDraftAtom,
  reportOriginVariantAtom,
  selectedJobApplicationAtom,
} from '../../../../employer-job-listing.atoms';
import { useCreateJobApplicationReport } from '../../../../hooks/use-job-application-report';
import { ReportReasonsFooter, ReportReasonsStep } from './report-reasons-step';
import { ReportConfirmationFooter, ReportConfirmationStep } from './report-confirmation-step';
import { ReportSuccess, ReportSuccessFooter } from './report-success';
import { getJobApplicationId } from '../../../../candidate-visibility';

export const REPORT_VARIANTS: CandidateModalVariantType[] = ['report', 'report_confirmation', 'report_success'];

const TOAST_CONFIG = { position: 'top-right', autoClose: 3000 } as const;

// Service errors reach the caller already formatted as `{ code, message, data }` (libs/data-services utils/axios).
const isConflict = (error: unknown) => (error as { code?: unknown } | null)?.code === 409;

/**
 * Owns the report-candidate flow and returns the body + footer JSX of the current report variant, so
 * candidate-modal can mount each into its slot. `null`/`null` outside the flow.
 *
 * Flow: report (reason, details, message) -> report_confirmation (recap, send) -> report_success.
 * The draft lives in `reportDraftAtom`, so going back from the recap keeps it.
 */
export function useReportCandidate() {
  const { t } = useTranslation('job-positions-page');
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [variant, setVariant] = useAtom(candidateModalVariantAtom);
  const originVariant = useAtomValue(reportOriginVariantAtom);
  const draft = useAtomValue(reportDraftAtom);
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const setIsCandidateModalSuccessScreen = useSetAtom(isCandidateModalSuccessScreenAtom);
  const { mutate, isPending } = useCreateJobApplicationReport();

  const jobApplicationId = getJobApplicationId(selectedJobApplication);

  const backToOrigin = useCallback(() => setVariant(originVariant), [originVariant, setVariant]);

  // Writes the known "sent" state into the cache before refetching it, so the header never shows
  // a clickable "Report" while the refetch runs (after a send, or after a 409).
  const markReportSent = useCallback(
    (report: JobApplicationReport) => {
      const queryKey = [QUERY_KEYS.WORKER_JOB_POSITIONS.JOB_APPLICATION_REPORT, jobApplicationId];
      queryClient.setQueryData(queryKey, report);
      queryClient.invalidateQueries({ queryKey });
    },
    [jobApplicationId, queryClient]
  );

  const submit = useCallback(() => {
    if (!jobApplicationId || !draft.reasonKey) return;
    const reasonKey = draft.reasonKey;
    const message = draft.message.trim();

    mutate(
      {
        jobApplicationId,
        params: { reason_key: reasonKey, detail_keys: draft.detailKeys, ...(message ? { message } : {}) },
      },
      {
        onSuccess: (response) => {
          markReportSent({
            id: response?.data?.id ?? '',
            inserted_at: response?.data?.inserted_at ?? new Date().toISOString(),
            reason_key: reasonKey,
          });
          trackEvent('employer_report_worker_sent', {
            job_application_id: jobApplicationId,
            reason_key: reasonKey,
            detail_count: draft.detailKeys.length,
            stage: selectedJobApplication.status_key,
            is_spontaneous: !selectedJobApplication.job_position_id,
          });
          setIsCandidateModalSuccessScreen(true);
          setVariant('report_success');
        },
        onError: (error) => {
          if (isConflict(error)) {
            // Already reported: show it as sent now; the refetch brings the real date.
            markReportSent({ id: '', inserted_at: new Date().toISOString(), reason_key: '' });
            backToOrigin();
            showToast(t('report.already_sent_toast'), 'reversed', TOAST_CONFIG);
            return;
          }
          showToast(t('report.error_toast'), 'error', TOAST_CONFIG);
        },
      }
    );
  }, [
    backToOrigin,
    draft,
    markReportSent,
    jobApplicationId,
    mutate,
    selectedJobApplication.job_position_id,
    selectedJobApplication.status_key,
    setIsCandidateModalSuccessScreen,
    setVariant,
    t,
    trackEvent,
  ]);

  const backToCandidate = useCallback(() => {
    setIsCandidateModalSuccessScreen(false);
    backToOrigin();
  }, [backToOrigin, setIsCandidateModalSuccessScreen]);

  if (variant === 'report')
    return {
      body: <ReportReasonsStep />,
      footer: <ReportReasonsFooter onCancel={backToOrigin} onContinue={() => setVariant('report_confirmation')} />,
    };
  if (variant === 'report_confirmation')
    return {
      body: <ReportConfirmationStep />,
      footer: (
        <ReportConfirmationFooter onBack={() => setVariant('report')} onSubmit={submit} isSubmitting={isPending} />
      ),
    };
  if (variant === 'report_success')
    return { body: <ReportSuccess />, footer: <ReportSuccessFooter onBackToCandidate={backToCandidate} /> };
  return { body: null, footer: null };
}
